import React from "react";
import log from "loglevel";
import { Dispatch } from "redux";
import { Action } from "typescript-fsa";
import { compose } from "recompose";
import { connect } from "react-redux";
import { Button } from "reactstrap";
import { FormattedMessage } from "react-intl";
import { Map } from "immutable";

import { Spinner } from "@ui/Spinner";
import { messages as uiMessages } from "@ui/messages";
import { StateError } from "@orm/models";
import { ApiState } from "../../models/state";
import { ApiRequest } from "../../requests";
import { getAllRequests } from "../../selectors";

interface UploadButtonStateProps {
    requests: Map<string, ApiRequest>;
}

interface UploadButtonDispatchProps {
    dispatchActionCreator: (file: File) => Action<ApiRequest>;
}

interface UploadButtonOwnProps {
    color?: string;
    className?: string;
    accept?: string;
    disabled?: boolean;
    children?: React.ReactNode;
    actionCreator: (file: File) => Action<ApiRequest>;
    onError?: (error: StateError) => void;
    onSuccess?: (response: any) => void;
}

interface UploadButtonState {
    currentRequest: ApiRequest;
    loading: boolean;
}

type Props = UploadButtonOwnProps & UploadButtonStateProps & UploadButtonDispatchProps;

class UploadButton extends React.Component<Props, UploadButtonState> {
    private inputRef: React.RefObject<HTMLInputElement>;

    constructor(props: Props) {
        super(props);

        // Initialise the state.
        this.state = {
            currentRequest: null,
            loading: false,
        };
        this.inputRef = React.createRef();
    }

    private handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { dispatchActionCreator } = this.props;
        if (dispatchActionCreator != null) {
            if (!this.state.loading) {
                const action = dispatchActionCreator(e.target.files[0]);
                this.setState({
                    currentRequest: action.payload,
                    loading: true,
                });
            }
        }
        else {
            log.error("Cannot perform an upload without a supplied 'actionCreator'");
        }
    }

    public componentWillReceiveProps(nextProps: Props) {
        // Check if the request has updated
        const { currentRequest } = this.state;
        const { onSuccess, onError } = this.props;
        if (currentRequest != null) {
            const nextRequest = nextProps.requests.get(currentRequest.uuid);
            // If the request exists and has changed state.
            if (nextRequest != null && currentRequest.state !== nextRequest.state) {
                const nextState: UploadButtonState = {
                    currentRequest: nextRequest,
                    loading: false,
                };
                // Set loading if in progress.
                if (nextRequest.isInProgress()) {
                    nextState.loading = true;
                }
                else if (nextRequest.isFinishedSucceeded() && onSuccess != null) {
                    // Clear the input
                    this.inputRef.current.value = "";
                    onSuccess(nextRequest.response);
                }
                else if (nextRequest.isFinishedFailed() && onError != null) {
                    // Clear the input
                    this.inputRef.current.value = "";
                    onError(nextRequest.error);
                }
                this.setState(nextState);
            }
        }
    }

    public render(): JSX.Element {
        const { children, className, color, accept, disabled } = this.props;
        const { loading } = this.state;
        const inputProps: React.HTMLProps<HTMLInputElement> = {
            key: "input",
            ref: this.inputRef,
            type: "file",
            onChange: this.handleChange.bind(this),
            disabled: loading || disabled,
        };
        if (accept != null) {
            inputProps.accept = accept;
        }
        return (
            <Button
                disabled={disabled == null ? false : disabled}
                tag="label"
                className={"upload-button" + (className != null ? " " + className : "")}
                color={color}
            >
                <input {...inputProps} />
                {loading &&
                    <span>
                        <Spinner inline />&nbsp;
                        <FormattedMessage {...uiMessages.loading} />
                    </span>
                }
                {!loading &&
                    <span key="children">{children}</span>
                }
            </Button>
        );
    }
}

const mapStateToProps = (state: ApiState, _props: UploadButtonOwnProps) => ({
    requests: getAllRequests(state),
} as UploadButtonStateProps);

const mapDispatchToProps = (dispatch: Dispatch<ApiState>, props: UploadButtonOwnProps) => {
    return {
        dispatchActionCreator: (file: File) => {
            const action = props.actionCreator(file);
            dispatch(action);
            return action;
        },
    } as UploadButtonDispatchProps;
};

const ConnectedUploadButton = compose<UploadButtonStateProps, UploadButtonOwnProps>(
    connect(mapStateToProps, mapDispatchToProps),
)(UploadButton);

export {
    UploadButton as UploadButtonPresenter,
    ConnectedUploadButton as UploadButton,
    Props,
    UploadButtonState,
};
